import type { SupportedLocale } from '../i18n';

/** `TAX_BASES` and `Money` mirror `backend/types/financialOperations.ts`; `previous` is the voided amount. */
export const TAX_BASES = ['HT', 'TTC'] as const;
export type TaxBasis = (typeof TAX_BASES)[number];

export interface Money {
  amountCents: number;
  taxBasis: TaxBasis;
  previous: { amountCents: number; taxBasis: TaxBasis; voidedAt: string } | null;
}

const NBSP = '\u00A0';

const TAX_LABELS: Record<SupportedLocale, Record<TaxBasis, string>> = {
  fr: { HT: 'HT', TTC: 'TTC' },
  en: { HT: 'excl. VAT', TTC: 'incl. VAT' },
  es: { HT: 'sin IVA', TTC: 'IVA incl.' },
};

/** R14 — « 180 € HT », « €180.50 incl. VAT »: integer cents; no-break spaces keep it on one line. */
export const formatMoney = (amountCents: number, taxBasis: TaxBasis, locale: SupportedLocale): string => {
  const euros = Math.trunc(amountCents / 100);
  const cents = amountCents % 100;
  const separator = locale === 'en' ? '.' : ',';
  const decimals = cents === 0 ? '' : `${separator}${String(cents).padStart(2, '0')}`;
  const basis = TAX_LABELS[locale][taxBasis].replace(/ /g, NBSP);
  return locale === 'en' ? `€${euros}${decimals}${NBSP}${basis}` : `${euros}${decimals}${NBSP}€${NBSP}${basis}`;
};

export const moneyText = (money: { amountCents: number; taxBasis: TaxBasis }, locale: SupportedLocale): string =>
  formatMoney(money.amountCents, money.taxBasis, locale);

/** Cents back to what the user types: « 180 », « 180,50 » (the inverse of `parseMoneyInput`). */
export const centsText = (cents: number, locale: SupportedLocale): string =>
  cents % 100 === 0
    ? String(cents / 100)
    : `${Math.trunc(cents / 100)}${locale === 'en' ? '.' : ','}${String(cents % 100).padStart(2, '0')}`;

const MONEY_INPUT_RE = /^\d+([.,]\d{1,2})?$/;
const MAX_CENTS = 2_147_483_647;

/** CA4 — « 180 », « 180,50 », « 180.5 » parse; text, negatives, >2 decimals or > the INTEGER cap refuse. */
export const parseMoneyInput = (text: string): number | null => {
  const trimmed = text.trim();
  if (!MONEY_INPUT_RE.test(trimmed)) return null;
  const [whole, fraction = ''] = trimmed.split(/[.,]/);
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return cents > MAX_CENTS ? null : cents;
};

export interface MoneyDraft {
  text: string;
  taxBasis: TaxBasis;
}

export const EMPTY_MONEY_DRAFT: MoneyDraft = { text: '', taxBasis: 'HT' };

/** `null` when empty, `undefined` when the text is not an amount, else the cents. */
export const parseMoneyDraft = (text: string): number | null | undefined =>
  text.trim() === '' ? null : parseMoneyInput(text) ?? undefined;

/** A total never mixes HT and TTC (« 216 € TTC + 40 € HT »): one sum per basis, nulls skipped. */
export const sumByBasis = (
  moneys: Array<{ amountCents: number; taxBasis: TaxBasis } | null | undefined>
): Totals => {
  const totals: Totals = {};
  for (const money of moneys) {
    if (!money) continue;
    totals[money.taxBasis] = (totals[money.taxBasis] ?? 0) + money.amountCents;
  }
  return totals;
};

export type Totals = Partial<Record<TaxBasis, number>>;

export const formatTotals = (totals: Totals, locale: SupportedLocale): string =>
  Object.entries(totals)
    .map(([basis, cents]) => formatMoney(cents ?? 0, basis as TaxBasis, locale))
    .join(' + ');

/** R26 — ordering only, on HT cents then TTC cents. */
export const byHtThenTtc = (a: Totals, b: Totals): number => (a.HT ?? 0) - (b.HT ?? 0) || (a.TTC ?? 0) - (b.TTC ?? 0);
